import { createFileRoute, Link } from "@tanstack/react-router";
import { serviceSchema } from "@/lib/service-schema";
import { breadcrumbSchema, faqSchema, webPageSchema } from "@/lib/schema";
import { PaginasRelacionadas } from "@/components/site/PaginasRelacionadas";
import { PageHero } from "@/components/site/PageHero";
import { PanelSection } from "@/components/site/PanelSection";
import { PageCta } from "@/components/site/PageCta";
import { AccesosRapidos } from "@/components/site/AccesosRapidos";
import { waLink } from "@/lib/whatsapp";
import { ogImageMeta } from "@/lib/og-image";

const TITLE = "Acompa\u00f1amiento post-hospitalario en Cuernavaca | Oasis";
const DESCRIPTION =
  "Acompañamiento cotidiano tras un alta hospitalaria en Cuernavaca, con presencia 24/7, habitación individual y comidas. Desde $950 MXN al día.";
const PATH = "/acompaamiento-post-hospitalario";

const FAQS = [
  {
    q: "¿Qué es el acompañamiento post-hospitalario?",
    a: "Es una estancia de acompañamiento después del alta indicada por su médico: su familiar permanece con nosotros durante su recuperación cotidiana, con presencia 24/7, habitación individual y comidas, y luego regresa a casa.",
  },
  {
    q: "¿Cuánto tiempo puede quedarse mi familiar?",
    a: "El tiempo que indique su recuperación. Es la única modalidad temporal que operamos: si al terminar la convalecencia la familia decide que necesita cuidado continuo, se pasa a residencia permanente.",
  },
  {
    q: "¿Cuánto cuesta la estancia de recuperación?",
    a: "Desde $950 pesos al día. La tarifa depende del nivel de acompañamiento cotidiano y apoyo en movilidad que requiera. Le damos el costo el mismo día.",
  },
  {
    q: "¿Qué no incluye la cuota?",
    a: "Pañales, insumos personales, estudios y consultas externas se pagan aparte. La cuota cubre estancia, comidas, lavandería, talleres y los servicios de la casa.",
  },
  {
    q: "¿Necesitan documentos del hospital para el ingreso?",
    a: "Sí: el resumen de alta hospitalaria. Con eso valoramos si podemos dar el nivel de cuidado que necesita antes de aceptar el ingreso.",
  },
];

export const Route = createFileRoute("/acompaamiento-post-hospitalario")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      {
        property: "og:url",
        content: "https://oasiscuernavaca.com/acompaamiento-post-hospitalario",
      },
      ...ogImageMeta(),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: "https://oasiscuernavaca.com/acompaamiento-post-hospitalario" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Acompa\u00f1amiento post-hospitalario para adultos mayores",
            serviceType: "Acompa\u00f1amiento post-hospitalario",
            description: DESCRIPTION,
            path: PATH,
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          webPageSchema({ name: TITLE, description: DESCRIPTION, path: PATH }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbSchema([{ name: "Acompañamiento post-hospitalario", path: PATH }]),
        ),
      },
    ],
  }),
  component: Page,
});

const PASOS = [
  ["1. Nos escribe con el resumen de alta", "Valoramos el caso el mismo día, sin compromiso."],
  ["2. Visita y valoración", "Conoce la habitación y acordamos los cuidados que requiere."],
  ["3. Ingreso acompañado", "Recibimos a su familiar y reportamos a la familia desde el día uno."],
] as const;

function Page() {
  const wa = waLink("acompañamiento post-hospitalario", "mi familiar sale del hospital");

  return (
    <>
      <PageHero
        breadcrumb="Acompañamiento post-hospitalario"
        title="Acompañamiento Post-Hospitalario en Cuernavaca"
        answer="Su familiar se recupera con acompañamiento 24/7 y habitación individual, y luego vuelve a casa."
        waHref={wa}
        waLabel="Escribir por WhatsApp"
      >
        <p className="mt-3 font-semibold">
          Desde $950 MXN al día, según el nivel de acompañamiento y cuidados.
        </p>
      </PageHero>

      <PanelSection
        tone="light"
        eyebrow="Cuándo"
        title="¿Cuándo conviene esta estancia?"
        answer="Cuando el alta llegó antes de que la casa estuviera lista para cuidarlo."
      >
        <ul className="mt-5 grid gap-2 sm:grid-cols-2">
          <li>Después de una cirugía de cadera o rodilla.</li>
          <li>Tras una caída con movilidad reducida.</li>
          <li>Al salir del hospital por neumonía o infección.</li>
          <li>Cuando necesita apoyo continuo en su rutina y movilidad.</li>
        </ul>
      </PanelSection>

      <PanelSection
        tone="soft"
        eyebrow="La estancia"
        title="¿Qué incluye la estancia?"
        answer="Habitación individual, tres comidas, lavandería, talleres y acompañamiento 24/7 durante la recuperación cotidiana."
      >
        <p className="mt-5">
          <Link to="/que-incluye" className="link-arrow">
            Ver la lista completa y qué se paga aparte
          </Link>
        </p>
      </PanelSection>

      <PanelSection
        tone="plain"
        eyebrow="Ingreso"
        title="¿Cómo es el ingreso?"
        answer="Tres pasos: nos escribe, visita y valoración, ingreso acompañado."
      >
        <ol className="mt-5 grid gap-4 sm:grid-cols-3">
          {PASOS.map(([t, d]) => (
            <li key={t}>
              <p className="font-semibold">{t}</p>
              <p className="mt-1 text-[0.95rem] text-muted-foreground">{d}</p>
            </li>
          ))}
        </ol>
      </PanelSection>

      <PanelSection tone="soft" eyebrow="Dudas" title="Preguntas sobre la estancia de recuperación">
        <div className="mt-5 divide-y divide-border border-t border-border">
          {FAQS.map((f) => (
            <details key={f.q} className="py-3">
              <summary className="faq-q">
                <h3 className="faq-h">{f.q}</h3>
              </summary>
              <p className="faq-a">{f.a}</p>
            </details>
          ))}
        </div>
      </PanelSection>

      <AccesosRapidos variant="panel" exclude={[PATH]} />

      <PageCta waHref={waLink("acompañamiento post-hospitalario", "quiero agendar una visita")} />

      <PaginasRelacionadas
        items={[
          {
            to: "/residencia-permanente",
            label: "Residencia permanente",
            hint: "Si al terminar la recuperación necesita cuidado continuo.",
          },
          {
            to: "/cuidado-alzheimer-demencia-cuernavaca",
            label: "Cuidado de residentes con demencia",
            hint: "Cómo cuidamos a quien tiene Alzheimer o demencia.",
          },
          {
            to: "/precios",
            label: "Precios y costos",
            hint: "Cotización el mismo día, sin compromiso.",
          },
          {
            to: "/preguntas-frecuentes",
            label: "Preguntas frecuentes",
            hint: "Costos, dietas, visitas y proceso de ingreso.",
          },
        ]}
      />
    </>
  );
}
